import { useMemo, useCallback, useState, type ReactNode } from "react";
import { useUserFiles } from "@/react/components/entityViews/files/api/files.api";
import type { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import InfiniteScroll from "@/modern/components/infinite-scroll";
import { Loader2 } from "lucide-react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn, formatRelativeTime } from "@/modern/utils";
import { usePostMountFlush } from "@/modern/hooks/usePostMountFlush";
import { useUsersContext } from "../../context/UsersContext";
import { TabEmpty, UsersTabDescription } from "../users-ui";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { INSIGHT_TYPES } from "@/bryntum-lib/models/Insights";
import type { BoardNote } from "@/bryntum-lib/models/BoardNote";
import type { Insights } from "@/bryntum-lib/models/Insights";
import type { Checklist } from "@/react/components/entityViews/checklist/types";
import { getFileExtension } from "@/modern/utils";
import { SWIVU_STANDARD_ICONS } from "@/other/constants/IconKeys.jsx";
import { VIEW_TYPES, ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";
import { WidgetParamKeys } from "@/types/entity";
import {
  USERS_HELPER_CLS,
  USERS_META_CLS,
  USERS_TITLE_CLS,
} from "../../constants/typography";

type ContentTabId = "files" | "notes" | "insights" | "checklists";
type ParentType = "resource" | "task" | "client";

const PARENT_TYPE_TO_ENTITY: Record<ParentType, string> = {
  task:     ENTITY_TYPES.TASK,
  resource: ENTITY_TYPES.RESOURCE,
  client:   ENTITY_TYPES.CLIENT,
};

function formatSize(bytes: number | string | null | undefined): string | null {
  const n = typeof bytes === "string" ? parseInt(bytes, 10) : bytes;
  if (!n || n <= 0 || isNaN(n as number)) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fileTypeIcon(name: string): ReactNode {
  const ext = getFileExtension(name ?? "");
  if (["pdf"].includes(ext)) return <i className="fa-light fa-file-pdf" />;
  if (["doc","docx","odt","rtf","txt","md"].includes(ext)) return <i className="fa-light fa-file-lines" />;
  if (["xls","xlsx","csv","ods"].includes(ext)) return <i className="fa-light fa-file-spreadsheet" />;
  if (["mp4","avi","mov","wmv","mkv","webm"].includes(ext)) return <i className="fa-light fa-file-video" />;
  if (["mp3","wav","ogg","m4a","flac","aac"].includes(ext)) return <i className="fa-light fa-file-audio" />;
  if (["js","ts","py","java","go","rs","php","html","css"].includes(ext)) return <i className="fa-light fa-file-code" />;
  return <i className="fa-light fa-file" />;
}

function isImageFile(name: string): boolean {
  return ["jpg","jpeg","png","gif","bmp","svg","webp","ico"].includes(getFileExtension(name ?? ""));
}

// ─── Parent entity chip ──────────────────────────────────────────────────────

const ENTITY_ICON_MAP = {
  resource: SWIVU_STANDARD_ICONS[VIEW_TYPES.resources] as string,
  task:     SWIVU_STANDARD_ICONS[VIEW_TYPES.tasks]     as string,
  client:   SWIVU_STANDARD_ICONS[VIEW_TYPES.clients]   as string,
} as const;

function EntityChip({ name, type }: { name: string; type: ParentType }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-border/60 bg-muted/40 text-sm font-normal leading-none text-muted-foreground max-w-[140px] truncate shrink-0">
      <i className={cn(ENTITY_ICON_MAP[type], "shrink-0 opacity-60 text-sm")} />
      <span className="truncate">{name}</span>
    </span>
  );
}

// ─── Shared row shell ────────────────────────────────────────────────────────

function ContentRow({
  onClick,
  children,
  archived,
}: {
  onClick?: () => void;
  children: ReactNode;
  archived?: boolean;
}) {
  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); }
          : undefined
      }
      className={cn(
        "group flex items-center gap-3 min-h-[64px] px-4 py-3.5 rounded-lg border",
        "border-border/50 bg-card transition-all duration-150 ease-out",
        onClick
          ? "cursor-pointer hover:bg-accent/50 hover:border-border/80 active:scale-[0.99]"
          : "hover:bg-muted/20 hover:border-border/70",
        archived && "opacity-55",
      )}
    >
      {children}
    </div>
  );
}

// ─── File thumbnail ──────────────────────────────────────────────────────────

function FileThumbnail({ file }: { file: SwivuFile }) {
  const [imgError, setImgError] = useState(false);
  const thumb = file.thumbnailUrl ?? undefined;
  const isImg = isImageFile(file.name ?? "");

  if (isImg && thumb && !imgError) {
    return (
      <div className="size-10 shrink-0 rounded-lg overflow-hidden border border-border/50 bg-muted/30">
        <img
          src={thumb}
          alt={file.name}
          className="size-full object-cover"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  return (
    <div className="size-10 shrink-0 rounded-lg overflow-hidden border border-border/50 bg-amber-500/10 text-amber-500 ring-1 ring-inset ring-amber-500/20 flex items-center justify-center text-base">
      {fileTypeIcon(file.name ?? "")}
    </div>
  );
}

// ─── Chevron indicator for clickable rows ────────────────────────────────────

function RowChevron() {
  return (
    <i className="fa-light fa-chevron-right text-sm text-muted-foreground/30 group-hover:text-muted-foreground/60 transition-colors duration-150 shrink-0" />
  );
}

// ─── Row components per content type ────────────────────────────────────────

function FileRow({
  file,
  parentName,
  parentType,
  onNavigate,
}: {
  file: SwivuFile;
  parentName?: string;
  parentType?: ParentType;
  onNavigate?: () => void;
}) {
  const ext = getFileExtension(file.name ?? "").toUpperCase() || "FILE";
  const sizeStr = formatSize(file.sizeBytes);

  return (
    <ContentRow onClick={onNavigate}>
      <FileThumbnail file={file} />

      <div className="min-w-0 flex-1">
        <p
          className={cn(
            USERS_TITLE_CLS,
            "truncate leading-tight",
          )}
        >
          {file.name ?? "Untitled"}
        </p>
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
          <span className="inline-flex items-center px-1 py-px rounded bg-muted text-sm font-medium leading-none text-muted-foreground ring-1 ring-inset ring-border/50 uppercase tracking-wider">
            {ext}
          </span>
          {sizeStr && (
            <span className={USERS_HELPER_CLS}>{sizeStr}</span>
          )}
          <span className="text-sm text-muted-foreground/50">·</span>
          <span className={USERS_HELPER_CLS}>{formatRelativeTime(file.createdAt)}</span>
        </div>
      </div>

      <div className="shrink-0 flex items-center gap-2">
        {parentName && parentType && (
          <EntityChip name={parentName} type={parentType} />
        )}
        {onNavigate && <RowChevron />}
      </div>
    </ContentRow>
  );
}

function NoteRow({
  note,
  parentName,
  parentType,
  onNavigate,
}: {
  note: BoardNote;
  parentName?: string;
  parentType?: ParentType;
  onNavigate?: () => void;
}) {
  return (
    <ContentRow onClick={onNavigate} archived={note.archived}>
      <div className="size-10 shrink-0 rounded-lg flex items-center justify-center bg-blue-500/10 text-blue-500 ring-1 ring-inset ring-blue-500/20 text-base">
        <i className="fa-light fa-note-sticky" />
      </div>

      <div className="min-w-0 flex-1">
        <p
          className={cn(
            USERS_TITLE_CLS,
            "truncate leading-tight",
            note.archived && "line-through opacity-60",
          )}
        >
          {note.name ?? "Untitled note"}
        </p>
        <p className={cn("mt-1.5", USERS_HELPER_CLS)}>
          {formatRelativeTime(note.createdAt)}
        </p>
      </div>

      <div className="shrink-0 flex items-center gap-2">
        {parentName && parentType && (
          <EntityChip name={parentName} type={parentType} />
        )}
        {onNavigate && <RowChevron />}
      </div>
    </ContentRow>
  );
}

const INSIGHT_LANG: Record<string, string> = { en: "EN", es: "ES" };

function InsightRow({ insight }: { insight: Insights }) {
  const meta = (INSIGHT_TYPES as any)[insight.insightType as string];
  const label = meta?.label ?? insight.insightType ?? "Insight";
  const preview = insight.content
    ? insight.content.replace(/<[^>]+>/g, "").trim().slice(0, 120)
    : null;
  const lang = INSIGHT_LANG[insight.language] ?? insight.language;

  return (
    <ContentRow>
      <div className="size-10 shrink-0 rounded-lg flex items-center justify-center bg-[hsl(var(--chart-4)/0.12)] text-[hsl(var(--chart-4))] ring-1 ring-inset ring-[hsl(var(--chart-4)/0.2)] text-base self-start mt-0.5">
        <i className="fa-light fa-lightbulb" />
      </div>

      <div className="min-w-0 flex-1">
        <p className={cn(USERS_TITLE_CLS, "leading-tight")}>{label}</p>
        {preview && (
          <p className={cn("mt-1.5 leading-relaxed line-clamp-2", USERS_HELPER_CLS)}>
            {preview}
          </p>
        )}
      </div>

      <div className="shrink-0 flex flex-col items-end gap-1 self-start mt-0.5">
        {lang && (
          <span className="inline-flex items-center px-1 py-px rounded bg-muted text-sm font-medium leading-none text-muted-foreground ring-1 ring-inset ring-border/50">
            {lang}
          </span>
        )}
        <span className={cn(USERS_HELPER_CLS, "whitespace-nowrap")}>
          {formatRelativeTime(insight.createdAt)}
        </span>
      </div>
    </ContentRow>
  );
}

function ChecklistRow({
  checklist,
  parentName,
  parentType,
  onNavigate,
}: {
  checklist: Checklist;
  parentName?: string;
  parentType?: ParentType;
  onNavigate?: () => void;
}) {
  const checked = checklist.checkedItems ?? 0;
  const unchecked = checklist.uncheckedItemsCount ?? 0;
  const total = checked + unchecked;
  const pct = total > 0 ? Math.round((checked / total) * 100) : 0;
  const done = total > 0 && checked === total;

  return (
    <ContentRow onClick={onNavigate} archived={checklist.archived}>
      <div
        className={cn(
          "size-10 shrink-0 rounded-lg flex items-center justify-center ring-1 ring-inset text-base",
          done
            ? "bg-success/10 text-success ring-success/20"
            : "bg-emerald-500/10 text-emerald-500 ring-emerald-500/20",
        )}
      >
        <i className="fa-light fa-list-check" />
      </div>

      <div className="min-w-0 flex-1">
        <p
          className={cn(
            USERS_TITLE_CLS,
            "truncate leading-tight",
            checklist.archived && "line-through opacity-60",
          )}
        >
          {checklist.title ?? "Untitled checklist"}
        </p>
        <p className={cn("mt-1.5", USERS_HELPER_CLS)}>
          {formatRelativeTime(checklist.createdAt)}
        </p>
        {total > 0 && (
          <div className="mt-2 h-[3px] max-w-[100px] rounded-full bg-muted/60 overflow-hidden">
            <div
              className={cn(
                "h-full rounded-full transition-all duration-300",
                done ? "bg-success" : "bg-emerald-500",
              )}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
      </div>

      <div className="shrink-0 flex items-center gap-2">
        {total > 0 && (
          <span
            className={cn(
              USERS_META_CLS,
              done ? "text-success" : "text-muted-foreground",
            )}
          >
            {checked}/{total}
          </span>
        )}
        {parentName && parentType && (
          <EntityChip name={parentName} type={parentType} />
        )}
        {onNavigate && <RowChevron />}
      </div>
    </ContentRow>
  );
}

// ─── Generic virtualizer ─────────────────────────────────────────────────────

function VirtualList<T>({
  items,
  estimateSize,
  renderItem,
  scrollElement,
}: {
  items: T[];
  estimateSize: number;
  renderItem: (item: T) => ReactNode;
  scrollElement: HTMLElement | null;
}) {
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => estimateSize,
    overscan: 5,
  });

  return (
    <div style={{ height: `${virtualizer.getTotalSize()}px`, position: "relative" }}>
      {virtualizer.getVirtualItems().map((vItem) => (
        <div
          key={vItem.key}
          data-index={vItem.index}
          ref={virtualizer.measureElement}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            transform: `translateY(${vItem.start}px)`,
          }}
        >
          <div className="pb-2">{renderItem(items[vItem.index])}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Main tab ────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ContentTab({ user }: { user: any }) {
  const { project, showArchived, tabScrollRef } = useUsersContext();
  const { entityManager, selectedBoard } = useSwivuBryntumContext();

  const { version: notesVersion } = useSwivuStore({ storeId: STORE_KEYS.boardNotesStore });
  const { version: insightsVersion } = useSwivuStore({ storeId: STORE_KEYS.insightsStore });
  const {
    files: allFiles,
    hasNextPage: filesHasNextPage,
    fetchNextPage: filesFetchNextPage,
    isFetchingNextPage: filesIsFetchingNextPage,
  } = useUserFiles({
    boardId: selectedBoard?.id ?? 0,
    creatorId: user.id,
    enabled: !!selectedBoard?.id,
  });

  const files = useMemo<SwivuFile[]>(
    () => allFiles.filter((f) => showArchived || !(f as any).archived),
    [allFiles, showArchived],
  );

  const notes = useMemo<BoardNote[]>(() => {
    if (!project) return [];
    return project.boardNotesStore.query(
      (n) => n.creatorId === user.id && (showArchived || !n.archived),
    );
  }, [user.id, notesVersion, project, showArchived]);

  const insights = useMemo<Insights[]>(() => {
    if (!project) return [];
    return project.insightsStore.query((ins) => ins.creator === user.id);
  }, [user.id, insightsVersion, project]);

  const checklists: Checklist[] = [];

  const resolveParent = useCallback((record: {
    taskId?: number | string | null;
    resourceId?: number | string | null;
    clientId?: number | string | null;
  }): { name: string; type: ParentType; id: number | string } | undefined => {
    if (!project) return undefined;
    if (record.taskId) {
      const entity = project.extraTaskStore.getById(record.taskId);
      if (entity?.name) return { name: entity.name, type: "task", id: record.taskId };
    }
    if (record.resourceId) {
      const entity = project.resourceStore.getById(record.resourceId);
      if (entity?.name) return { name: entity.name, type: "resource", id: record.resourceId };
    }
    if (record.clientId) {
      const entity = project.clientsStore.getById((record as any).clientId);
      if (entity?.name) return { name: entity.name, type: "client", id: (record as any).clientId };
    }
    return undefined;
  }, [project]);

  const navigateToParent = useCallback((
    parent: { type: ParentType; id: number | string },
    otherParams?: Record<string, number | string | boolean>,
  ) => {
    entityManager?.openEntityManager({
      entityType: PARENT_TYPE_TO_ENTITY[parent.type],
      entityId: parent.id,
      ...(otherParams ? { otherParams } : {}),
    });
  }, [entityManager]);

  const tabs: Array<{ id: ContentTabId; label: string; count: number; faIcon: string }> = [
    { id: "files",      label: "Files",      count: files.length,      faIcon: "fa-light fa-folder-open" },
    { id: "notes",      label: "Notes",      count: notes.length,      faIcon: "fa-light fa-note-sticky" },
    { id: "insights",   label: "Insights",   count: insights.length,   faIcon: "fa-light fa-lightbulb" },
    { id: "checklists", label: "Checklists", count: checklists.length, faIcon: "fa-light fa-list-check" },
  ];

  const defaultTab = tabs[0]?.id ?? "files";
  const [active, setActive] = useState<ContentTabId>(defaultTab);

  usePostMountFlush();

  const scrollEl = tabScrollRef.current;

  let panel: ReactNode = null;

  if (active === "files") {
    panel = files.length === 0 ? (
      <TabEmpty
        icon="fa-light fa-file"
        message="No files uploaded by this user."
        hint="Files attached to any entity where this user is the creator will appear here."
      />
    ) : (
      <>
        <VirtualList
          items={files}
          estimateSize={72}
          scrollElement={scrollEl}
          renderItem={(f) => {
            const parent = resolveParent({ taskId: f.task, resourceId: f.resource, clientId: f.client });
            return (
              <FileRow
                file={f}
                parentName={parent?.name}
                parentType={parent?.type}
                onNavigate={parent ? () => navigateToParent(parent, { [WidgetParamKeys.FILE.EDIT]: f.id }) : undefined}
              />
            );
          }}
        />
        {(filesHasNextPage || filesIsFetchingNextPage) && (
          <InfiniteScroll
            isLoading={filesIsFetchingNextPage}
            hasMore={filesHasNextPage}
            next={filesFetchNextPage}
            rootMargin="50px"
            threshold={0}
          >
            <div className="flex items-center justify-center py-4">
              {filesIsFetchingNextPage && (
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              )}
            </div>
          </InfiniteScroll>
        )}
      </>
    );
  } else if (active === "notes") {
    panel = notes.length === 0 ? (
      <TabEmpty
        icon="fa-light fa-note-sticky"
        message="No notes created by this user."
        hint="Notes authored by this user across any entity will appear here."
      />
    ) : (
      <VirtualList
        items={notes}
        estimateSize={72}
        scrollElement={scrollEl}
        renderItem={(n) => {
          const parent = resolveParent(n);
          return (
            <NoteRow
              note={n}
              parentName={parent?.name}
              parentType={parent?.type}
              onNavigate={parent ? () => navigateToParent(parent, { [WidgetParamKeys.NOTE.EDIT]: n.id }) : undefined}
            />
          );
        }}
      />
    );
  } else if (active === "insights") {
    panel = insights.length === 0 ? (
      <TabEmpty
        icon="fa-light fa-lightbulb"
        message="No insights created by this user."
      />
    ) : (
      <VirtualList
        items={insights}
        estimateSize={72}
        scrollElement={scrollEl}
        renderItem={(ins) => <InsightRow insight={ins} />}
      />
    );
  } else if (active === "checklists") {
    panel = checklists.length === 0 ? (
      <TabEmpty
        icon="fa-light fa-list-check"
        message="No checklists created by this user."
        hint="Checklists authored by this user across any entity will appear here."
      />
    ) : (
      <VirtualList
        items={checklists}
        estimateSize={72}
        scrollElement={scrollEl}
        renderItem={(c) => {
          const parent = resolveParent(c);
          return (
            <ChecklistRow
              checklist={c}
              parentName={parent?.name}
              parentType={parent?.type}
              onNavigate={parent ? () => navigateToParent(parent, { [WidgetParamKeys.CHECKLIST.EDIT]: c.id }) : undefined}
            />
          );
        }}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Summary KPI */}
      <UsersTabDescription className="mt-0 max-w-prose">
        Files, notes, insights, and checklists this user created on the board.
      </UsersTabDescription>

      {/* Sub-tab nav */}
      <div>
        <nav className="border-b border-border/70">
          <div className="flex gap-5 overflow-x-auto overflow-y-hidden">
            {tabs.map((t) => {
              const on = t.id === active;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActive(t.id)}
                  className={cn(
                    "inline-flex items-center gap-1.5 pb-2.5 pt-0.5 text-base transition-colors border-b-2 -mb-px whitespace-nowrap font-normal",
                    on
                      ? "text-foreground border-primary"
                      : "text-muted-foreground hover:text-foreground border-transparent",
                  )}
                >
                  <i className={cn(t.faIcon, "shrink-0 opacity-80 text-base")} aria-hidden />
                  {t.label}
                  <span
                    className={cn(
                      "inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-full tabular-nums text-sm font-medium leading-none",
                      on
                        ? "bg-muted text-foreground"
                        : "bg-muted/50 text-muted-foreground",
                    )}
                  >
                    {t.count}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        <div className="pt-4">{panel}</div>
      </div>
    </div>
  );
}
